import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Divider,
  Link
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getContrastText } from '../utils/colorUtils';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.png';
import logo2 from '../assets/logo2.png';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

function PageLayout({ title, onAdd, hideAddButton, children, customColors, actionButtons, addButtonText, hideLogo, hideTitle, hideFooter, compactHeader, hideActionHeader }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const isDarkMode = theme.palette.mode === 'dark';
  const [userData, setUserData] = useState({});

  useEffect(() => {
    if (currentUser?.uid) {
      const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (docSnap) => {
        if (docSnap.exists()) {
          setUserData(docSnap.data());
        }
      });
      return () => unsub();
    }
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100%',
      width: '100%',
      overflow: 'visible'
    }}>
      {/* Logo centralizado */}
      {!hideLogo && (
        <Box
          component="div"
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 1,
            backgroundColor: 'background.default',
            mb: { xs: 1, sm: 4 }
          }}
        >
          <Box
            component="img"
            src={isDarkMode ? logo2 : logo}
            alt="Habitop Logo"
            sx={{
              height: { xs: 90, sm: 100 },
              width: 'auto',
              mb: 0,
              mt: -5
            }}
          />
        </Box>
      )}

      {/* Conteúdo principal */}
      <Box sx={{ 
        p: { xs: 0.5, sm: 3 },
        pt: { xs: 0.5, sm: 2 },
        maxWidth: 1800,
        mx: 'auto',
        width: '100%',
        flex: 1,
        overflow: 'visible'
      }}>
        {!hideActionHeader && (
          <Box sx={{
            mb: { xs: 1, sm: 4 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            gap: { xs: 1, sm: 0 },
            minHeight: { xs: 48, sm: 'auto' }
          }}>
            {!hideTitle && !isMobile && (
              <Typography
                variant="h4"
                fontWeight="bold"
                color="primary.main"
                sx={{ fontSize: '2rem', flexShrink: 0, mb: 0, mt: 0 }}
              >
                {title}
              </Typography>
            )}
            {actionButtons && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0, width: { xs: '100%', sm: 'auto' } }}>{actionButtons}</Box>
            )}
          </Box>
        )}
        {children}
      </Box>

      {/* Rodapé */}
      {!hideFooter && (
        <Box sx={{ 
          mt: 'auto',
          width: '100%',
          p: { xs: 1, sm: 2 }
        }}>
          <Divider />
          <Box sx={{ 
            mt: 2,
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: customColors?.primary ? `${customColors.primary}99` : 'text.secondary'
              }}
            >
              {userData?.email || currentUser?.email}
            </Typography>
            {(userData?.name || currentUser?.displayName) && (
              <Typography 
                variant="body2"
                sx={{ 
                  color: customColors?.primary ? `${customColors.primary}99` : 'text.secondary',
                  fontWeight: 500
                }}
              >
                {userData?.name || currentUser?.displayName}
              </Typography>
            )}
            <Link
              component="button"
              variant="body2"
              onClick={handleLogout}
              sx={{
                color: customColors?.primary ? `${customColors.primary}99` : 'text.secondary',
                textDecoration: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                '&:hover': {
                  textDecoration: 'underline',
                  color: customColors?.primary || 'primary.main'
                }
              }}
            >
              (sair)
            </Link>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default PageLayout; 